/**
 * Lo que dice el TEXTO de una factura recibida — `OD-0061 D-02` (ACCEPTED
 * 2026-10-24, opción A: todo en el navegador, sin IA).
 *
 * El texto llega de `lector-factura.ts` (el que ya lleva el PDF, o el que lee
 * Tesseract de una foto). Aquí solo hay reglas, sin DOM ni red, para poder
 * probarlas con facturas de ejemplo (`test:lectura-factura`).
 *
 * La regla de oro: **proponer solo lo que cuadra**. Un NIF sin su dígito de
 * control no se propone; un desglose que no suma exactamente un total de la
 * propia factura, tampoco. Lo que no se propone lo escribe el usuario, como
 * hasta hoy, y la API lo valida todo al guardar.
 *
 * El dinero va en céntimos enteros (`bigint`), nunca en coma flotante
 * (`CLAUDE.md`, prohibiciones). Y aquí no se CALCULA ningún importe para
 * enseñarlo: `base × tipo` solo sirve para RECONOCER qué cifras de la factura
 * son una base y su cuota. Lo que se propone son las cifras que escribe la
 * factura, y los totales del formulario los sigue dando la API
 * (`POST /purchases/calculate`). Sin conversiones a número de coma flotante en todo el
 * fichero, como en el resto de la web (`test:web-shell`).
 */

/** Los tipos que admite el formulario (`TIPOS_IVA_COMPRA`), sin el 0 %: una base sola no se distingue de cualquier importe. */
const TIPOS_IVA = [21n, 10n, 4n] as const;
/** Los de `TIPOS_RETENCION_COMPRA`. */
const TIPOS_RETENCION = [15n, 7n, 19n, 1n] as const;
/** Un céntimo de margen: la factura redondea la cuota a su manera (`OD-0054 D-03`). */
const MARGEN = 1n;
const MAX_LINEAS = 3;

export type LineaLeida = { readonly tipo: string; readonly base: string; readonly cuota: string };

export type PropuestaFactura = {
  readonly nif: string | null;
  readonly nombre: string | null;
  readonly numero: string | null;
  /** AAAA-MM-DD. */
  readonly fecha: string | null;
  readonly vencimiento: string | null;
  /** `null` si no se ha encontrado un desglose que cuadre con un total. */
  readonly lineas: readonly LineaLeida[] | null;
  readonly retencion: { readonly tipo: string; readonly importe: string } | null;
};

// ------------------------------------------------------------------ dinero

/** "1234,56" (la coma del formulario, sin miles). */
export function comoImporte(centimos: bigint): string {
  const negativo = centimos < 0n;
  const v = negativo ? -centimos : centimos;
  const euros = v / 100n;
  const cent = v % 100n;
  return `${negativo ? "-" : ""}${euros.toString()},${cent.toString().padStart(2, "0")}`;
}

/** Un tipo como lo escribe el formulario: "21,00". */
const comoTipo = (tipo: bigint): string => `${tipo.toString()},00`;

/** `round(base × tipo / 100)` al céntimo, mitad hacia arriba. */
export function cuotaDe(base: bigint, tipo: bigint): bigint {
  return (base * tipo + 50n) / 100n;
}

const cerca = (a: bigint, b: bigint): boolean => (a > b ? a - b : b - a) <= MARGEN;

/**
 * Los importes con DOS decimales de un texto, en céntimos. Vale "1.234,56",
 * "1234,56", "1,234.56" y "1234.56". No vale lo que sigue con "%" (un tipo) ni
 * lo que forma parte de una fecha ("12.10.2026").
 */
export function importesDe(texto: string): bigint[] {
  const re = /(?<![\d.,/-])(-?)(\d{1,3}(?:\.\d{3})+,\d{2}|\d{1,3}(?:,\d{3})+\.\d{2}|\d+[.,]\d{2})(?![\d]|[.,/-]\d)(?!\s*%)/g;
  const out: bigint[] = [];
  for (const m of texto.matchAll(re)) {
    const crudo = m[2] ?? "";
    const decimal = crudo.slice(-3, -2);
    const entero = crudo.slice(0, -3).replace(decimal === "," ? /\./g : /,/g, "");
    if (!/^\d+$/.test(entero)) continue;
    const v = BigInt(entero) * 100n + BigInt(crudo.slice(-2));
    out.push(m[1] === "-" ? -v : v);
  }
  return out;
}

// --------------------------------------------------------------------- NIF

const LETRAS_DNI = "TRWAGMYFPDXBNJZSQVHLCKE";
const CIFRAS = "0123456789";

/** "0412" → 412. Solo cifras: lo garantiza la expresión que lo extrae. */
const entero = (s: string): number => [...s].reduce((n, c) => n * 10 + CIFRAS.indexOf(c), 0);

/** NIF de persona, NIE o CIF con su dígito de control. El mismo cálculo que `customers/domain/tax-id.ts` en la API. */
export function nifValido(nif: string): boolean {
  if (/^\d{8}[A-Z]$/.test(nif)) return LETRAS_DNI[entero(nif.slice(0, 8)) % 23] === nif[8];
  if (/^[XYZ]\d{7}[A-Z]$/.test(nif)) return LETRAS_DNI[entero(`${"XYZ".indexOf(nif[0] ?? "")}${nif.slice(1, 8)}`) % 23] === nif[8];
  if (/^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(nif)) {
    const d = [...nif.slice(1, 8)].map((c) => CIFRAS.indexOf(c));
    let suma = 0;
    d.forEach((x, i) => {
      if (i % 2 === 1) suma += x;
      else {
        const doble = x * 2;
        suma += Math.floor(doble / 10) + (doble % 10);
      }
    });
    const control = (10 - (suma % 10)) % 10;
    const c = nif[8] ?? "";
    return c === String(control) || c === "JABCDEFGHI"[control];
  }
  return false;
}

/** "ES-B12.345.674" → "B12345674". */
export function normalizarNif(nif: string): string {
  return nif.toUpperCase().replace(/[\s.\-]/g, "").replace(/^ES(?=[0-9A-Z]{9}$)/, "");
}

/** Los NIF válidos del texto, en el orden en que aparecen y sin repetir. */
export function nifsDe(texto: string): string[] {
  const t = texto.toUpperCase().replace(/(\d)\.(?=\d{3}\b)/g, "$1");
  const re = /(?<![A-Z0-9])(?:ES[\s-]?)?([XYZ]\d{7}|\d{8}|[ABCDEFGHJNPQRSUVW][\s-]?\d{7})[\s-]?([A-Z0-9])(?![A-Z0-9])/g;
  const vistos: string[] = [];
  for (const m of t.matchAll(re)) {
    const nif = normalizarNif(`${m[1] ?? ""}${m[2] ?? ""}`);
    if (nifValido(nif) && !vistos.includes(nif)) vistos.push(nif);
  }
  return vistos;
}

// ------------------------------------------------------------------- fechas

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function iso(d: number, m: number, a: number): string | null {
  const anio = a < 100 ? 2000 + a : a;
  if (m < 1 || m > 12 || d < 1 || anio < 2000 || anio > 2099) return null;
  const dias = new Date(Date.UTC(anio, m, 0)).getUTCDate();
  if (d > dias) return null;
  return `${anio}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Las fechas de una línea: "15/10/2026", "15-10-26", "15.10.2026" o "15 de octubre de 2026". */
export function fechasDe(linea: string): string[] {
  const out: string[] = [];
  for (const m of linea.matchAll(/(?<!\d)(\d{1,2})[/.-](\d{1,2})[/.-](\d{4}|\d{2})(?!\d)/g)) {
    const f = iso(entero(m[1] ?? ""), entero(m[2] ?? ""), entero(m[3] ?? ""));
    if (f !== null) out.push(f);
  }
  for (const m of linea.toLowerCase().matchAll(/(?<!\d)(\d{1,2})\s+de\s+([a-zñ]+)\s+(?:de|del)\s+(\d{4})(?!\d)/g)) {
    const f = iso(entero(m[1] ?? ""), MESES.indexOf(m[2] ?? "") + 1, entero(m[3] ?? ""));
    if (f !== null) out.push(f);
  }
  return out;
}

// --------------------------------------------------------- número y nombre

/** "n.º", "nº", "n°", "no"; y "n.9", que es como Tesseract lee a menudo la "º" voladita. */
const ETIQUETA_NUMERO =
  /(?:n\.?\s*(?:[º°o]|9(?=\s))\.?\s*(?:de\s+)?factura|factura\s*(?:n\.?\s*(?:[º°o]|9(?=\s))\.?|n[úu]m(?:ero)?\.?)|n[úu]mero\s+(?:de\s+)?factura|n[úu]m\.?\s+(?:de\s+)?factura|invoice\s*(?:no\.?|number|#))\s*[:.#]?\s*([A-Z0-9][A-Z0-9\-/._]{0,59})/i;

function numeroDe(lineas: readonly string[]): string | null {
  const conCifra = (s: string | undefined): string | null => {
    const v = (s ?? "").replace(/[._-]+$/, "");
    return /\d/.test(v) && fechasDe(v).length === 0 ? v : null;
  };
  for (const l of lineas) {
    const n = conCifra(l.match(ETIQUETA_NUMERO)?.[1]);
    if (n !== null) return n;
  }
  for (const l of lineas) {
    const n = conCifra(l.match(/^\s*factura\s*[:#]?\s*([A-Z0-9][A-Z0-9\-/._]{0,59})\s*$/i)?.[1]);
    if (n !== null) return n;
  }
  return null;
}

const HASTA_FORMA_JURIDICA = /^(.*?[A-ZÁÉÍÓÚÑ].*?\b(?:S\.?\s?L\.?(?:\s?U\.?|\s?L\.?|\s?P\.?)?|S\.?\s?A\.?(?:\s?U\.?)?|S\.?\s?COOP\.?|C\.?\s?B\.?))(?=[\s,;]|$)/i;

/** Una línea del destinatario: su nombre es el de la organización, no el del proveedor. */
const DESTINATARIO = /^\s*(?:cliente|destinatario|facturar\s+a|datos\s+del\s+cliente)\b/i;

function nombreDe(lineas: readonly string[], nif: string | null, propio: string | null): string | null {
  const limpia = (l: string): string => {
    const sinEtiqueta = l.replace(/^\s*(?:raz[óo]n\s+social|emisor|proveedor|de)\s*[:.-]\s*/i, "").trim();
    // El NIF al final de la línea ("Turia S.A.  A28173649", "(CIF: B-87654323)"), solo si lo es.
    const m = sinEtiqueta.match(/\s*\(?\s*(?:(?:N\.?I\.?F|C\.?I\.?F)\.?\s*[:.]?\s*)?((?:ES[\s-]?)?[A-Z0-9][A-Z0-9.-]{7,12})\)?$/i);
    return m !== null && nifValido(normalizarNif(m[1] ?? "")) ? sinEtiqueta.slice(0, m.index).trim() : sinEtiqueta;
  };
  const ajena = (l: string): boolean => !DESTINATARIO.test(l) && (propio === null || !normalizarNif(l).includes(propio));
  const donde = nif === null ? -1 : lineas.findIndex((l) => normalizarNif(l).includes(nif));
  const alrededor = donde < 0 ? [] : lineas.slice(Math.max(0, donde - 3), donde + 1).reverse();
  for (const l of [...alrededor, ...lineas.slice(0, 8)].filter(ajena)) {
    // Hasta la forma jurídica: lo que sigue en la línea suele ser ruido del OCR o el NIF.
    const n = limpia(l).match(HASTA_FORMA_JURIDICA)?.[1]?.trim() ?? "";
    if (n.length >= 3 && n.length <= 120) return n;
  }
  return null;
}

// ----------------------------------------------------------------- desglose

type Pareja = { readonly base: bigint; readonly tipo: bigint; readonly cuota: bigint };

function combinaciones<T>(xs: readonly T[], k: number): T[][] {
  if (k === 0) return [[]];
  return xs.flatMap((x, i) => combinaciones(xs.slice(i + 1), k - 1).map((r) => [x, ...r]));
}

/** Los importes de las líneas que dicen "total" (o de la siguiente, si la cifra va debajo). */
function totalesDe(lineas: readonly string[]): bigint[] {
  const out: bigint[] = [];
  lineas.forEach((l, i) => {
    if (!/total/i.test(l)) return;
    const aqui = importesDe(l);
    out.push(...(aqui.length > 0 ? aqui : importesDe(lineas[i + 1] ?? "")));
  });
  return out.filter((v) => v > 0n);
}

function desgloseDe(texto: string, lineas: readonly string[]): Pick<PropuestaFactura, "lineas" | "retencion"> {
  const nada = { lineas: null, retencion: null };
  // En valor absoluto: la retención suele ir con signo ("-120,00").
  const importes = [...new Set(importesDe(texto).map((v) => (v < 0n ? -v : v)).filter((v) => v > 0n))];
  const hay = (v: bigint): bigint | undefined => importes.find((x) => cerca(x, v));
  const totales = totalesDe(lineas);
  if (totales.length === 0) return nada;

  const parejas: Pareja[] = [];
  for (const base of importes) {
    for (const tipo of TIPOS_IVA) {
      const cuota = hay(cuotaDe(base, tipo));
      if (cuota !== undefined && cuota > 0n && cuota !== base) parejas.push({ base, tipo, cuota });
    }
  }

  const conRetencion = /irpf|retenci[óo]n|\bret\.?\s/i.test(texto);
  const mencionado = (tipo: bigint): boolean => new RegExp(`(?<![\\d,.])${tipo.toString()}(?:[.,]0{1,2})?\\s*%`).test(texto);

  type Solucion = { readonly parejas: readonly Pareja[]; readonly retencion: { tipo: bigint; importe: bigint } | null; readonly total: bigint };
  const soluciones: Solucion[] = [];
  for (let k = 1; k <= MAX_LINEAS; k++) {
    for (const ps of combinaciones(parejas, k)) {
      if (new Set(ps.map((p) => p.tipo)).size !== k) continue;
      const usados = ps.flatMap((p) => [p.base, p.cuota]);
      if (new Set(usados).size !== usados.length) continue;
      const bases = ps.reduce((s, p) => s + p.base, 0n);
      const suma = ps.reduce((s, p) => s + p.base + p.cuota, 0n);
      let retencion: Solucion["retencion"] = null;
      if (conRetencion) {
        for (const tipo of TIPOS_RETENCION) {
          const importe = hay(cuotaDe(bases, tipo));
          if (importe !== undefined && !usados.includes(importe)) {
            retencion = { tipo, importe };
            break;
          }
        }
      }
      const cuadraFactura = totales.some((t) => cerca(t, suma));
      const cuadraAPagar = retencion !== null && totales.some((t) => cerca(t, suma - retencion.importe));
      if (cuadraFactura || cuadraAPagar) soluciones.push({ parejas: ps, retencion, total: suma });
    }
  }
  if (soluciones.length === 0) return nada;

  // El total de la factura es el mayor; entre los que lo dan, mejor si sus
  // tipos aparecen escritos y con menos líneas. Dos desgloses igual de buenos
  // y distintos: no se propone ninguno.
  const nota = (s: Solucion): [bigint, number, number] => [s.total, s.parejas.every((p) => mencionado(p.tipo)) ? 1 : 0, -s.parejas.length];
  const mejor = (a: Solucion, b: Solucion): number => {
    const [x, y] = [nota(a), nota(b)];
    if (x[0] !== y[0]) return x[0] > y[0] ? -1 : 1;
    return y[1] - x[1] || y[2] - x[2];
  };
  soluciones.sort(mejor);
  const [primera, segunda] = soluciones;
  if (primera === undefined) return nada;
  if (segunda !== undefined && mejor(primera, segunda) === 0) return nada;

  return {
    lineas: [...primera.parejas]
      .sort((a, b) => (a.tipo > b.tipo ? -1 : a.tipo < b.tipo ? 1 : 0))
      .map((p) => ({ tipo: comoTipo(p.tipo), base: comoImporte(p.base), cuota: comoImporte(p.cuota) })),
    retencion: primera.retencion === null ? null : { tipo: comoTipo(primera.retencion.tipo), importe: comoImporte(primera.retencion.importe) },
  };
}

// -------------------------------------------------------------- la factura

/**
 * La propuesta para el formulario a partir del texto de una factura. `nifPropio`
 * es el de la organización que la recibe: también sale en la factura y no es
 * el del proveedor.
 */
export function propuestaDelTexto(texto: string, nifPropio: string | null): PropuestaFactura {
  const lineas = texto
    .split(/\r?\n/)
    .map((l) => l.replace(/[ \t]+/g, " ").trim())
    .filter((l) => l !== "");
  const propio = nifPropio === null ? null : normalizarNif(nifPropio);
  const nif = nifsDe(texto).find((n) => n !== propio) ?? null;

  const esVencimiento = (l: string): boolean => /venc/i.test(l);
  const conFecha = lineas.filter((l) => /fecha/i.test(l) && !esVencimiento(l) && !/operaci|entrega|pago|validez|v[aá]lid/i.test(l));
  const fecha = conFecha.flatMap(fechasDe)[0] ?? lineas.filter((l) => !esVencimiento(l)).flatMap(fechasDe)[0] ?? null;
  const vencimiento = lineas.filter(esVencimiento).flatMap(fechasDe)[0] ?? null;

  return {
    nif,
    nombre: nombreDe(lineas, nif, propio),
    numero: numeroDe(lineas),
    fecha,
    vencimiento: vencimiento !== null && fecha !== null && vencimiento < fecha ? null : vencimiento,
    ...desgloseDe(lineas.join("\n"), lineas),
  };
}
